/**
 * @author: lv.chen
 * @date: 2025/6/27
 * @desc: 表格组件，基于Tea组件库的Table组件二次封装
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Table, TableProps, TableAddon, PagingQuery } from 'tea-component';
import useTableAutotipAddon from './_hooks/useTableAutotipAddon';
import useTablePageableAddon from './_hooks/useTablePageableAddon';

import type { IRecord } from '@home/types/common';

import './index.scss';

export interface IDBTableProps extends IBaseProps, TableProps {
  // 是否加载数据中
  loading?: boolean;
  // 是否开启表头吸顶效果，默认开启
  useHeaderSticky?: boolean;
  // 是否开启分页
  usePagination?: boolean;
  // 数据总条数
  total?: number;
  // 当前分页
  pagingQuery?: PagingQuery;
  // 分页change回调
  onPagingChange?: (paging: PagingQuery) => void;
  // 分页大小选项
  pageSizeOptions?: number[];
  // 空数据提示文案
  emptyText?: React.ReactNode;
  // 额外的 Table addons（如 expandable）
  extraAddons?: TableAddon[];
}

export default function DBTable<T = IRecord>(props: IDBTableProps) {
  const {
    style,
    className,
    loading = true,
    useHeaderSticky = true,
    usePagination = true,
    pagingQuery,
    total: totalProps,
    onPagingChange,
    pageSizeOptions,
    emptyText,
    extraAddons,
    records,
    ...restProps
  } = props;

  const total = totalProps ?? records?.length ?? 0;

  const autotipAddon = useTableAutotipAddon<T>({ isLoading: false, emptyText });

  const pageableAddon = useTablePageableAddon<T>({
    ...pagingQuery,
    onPagingChange,
    loading,
    recordCount: loading ? 0 : total,
    pageSizeOptions,
  });

  const addons = useMemo<TableAddon<T>[]>(() => {
    const results: TableAddon<T>[] = [autotipAddon, ...(extraAddons || [])];
    if (usePagination) {
      results.push(pageableAddon);
    }
    return results;
  }, [usePagination, autotipAddon, pageableAddon, extraAddons]);

  return (
    <Table
      {...restProps}
      style={style}
      className={classnames('db-table', className, { 'db-table--header-sticky': useHeaderSticky })}
      addons={addons}
      records={records}
    />
  );
}
